import _ from 'lodash'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Icon, Label } from 'semantic-ui-react'
import { ParentContext } from '../../../../global/cnr/contexts/ParentContext'
import { uniqueKey } from '../../../../global/common/keys'
import { gEnums } from '../../../../global/enums/globalEnums'
import { SportsDataContext } from '../../cnr/contexts/SportsDataContext'
import SportsMatchProvider, { SportsMatchContext } from '../../cnr/contexts/SportsMatchContext'
import SportsRtMatchProvider from '../../cnr/contexts/SportsRtMatchContext'
import { SportsSeasonContext } from '../../cnr/contexts/SportsSeasonContext'
import { CreatePlayoffContext } from '../../create/CreatePlayoff'
import MatchDivs from './MatchDivs'
import MatchScores from './MatchScores'
import { matchFilterHelpers } from './MatchFilters'
import { _longPressTypes, LongPressWrapper } from 'global/wrappers/LongPressWrapper'
import { _allowUpdate_local } from 'projectSpecific/sports/useHelpers/useSeasonals'
import { MatchResultSquares } from './MatchResultSquares'
import { useMatchVideos } from 'projectSpecific/sports/useHelpers/useMatchVideos'
import useTimeMeasurement from 'global/cnr/reducers/reducerHelpers/validTimer'
import { useMatchDisplayType } from 'projectSpecific/sports/useHelpers/useMatchDisplayType'

export const _matchDisplayTypes = {
  levelMatches_playoffs: 'levelMatches_playoffs',
  levelMatches: 'levelMatches',
  levelMatchNoScore_playoffs: 'levelMatchNoScore_playoffs',
  levelMatchNoScore: 'levelMatchNoScore',
  levelOverviewMatches: 'levelOverviewMatches',
  playoff_grid_noScore_pending: 'playoff_grid_noScore_pending',
  playoff_grid_pending: 'playoff_grid_pending',
  playoff_grid_pendingA: 'playoff_grid_pendingA',
  playoff_grid_noScore_pendingA: 'playoff_grid_noScore_pendingA',
  playoff_grid_noScore: 'playoff_grid_noScore',
  playoff_grid: 'playoff_grid',
  playoff_noScore: 'playoff_noScore',
  playoff: 'playoff',
  playoffMatchAwayPending: 'playoffMatchAwayPending',
  playoffMatchPending: 'playoffMatchPending',
  playoffPending: 'playoffPending',
  playoffPendingAway: 'playoffPendingAway',
  scoreboard_playoffs: 'scoreboard_playoffs',
  scoreboard: 'scoreboard',
  teamMatches: 'teamMatches',
  teamMatchesNoScore_playoffs: 'teamMatchesNoScore_playoffs',
  teamMatchesNoScore: 'teamMatchesNoScore',
  teamMatchNoScore: 'teamMatchNoScore',
}

const matchPropTypes = Object.freeze({
  startDate: 'startDate',
  startTime: 'startTime',
  awayName: 'away.name',
  homeName: 'home.name',
  opponentName: 'opponent.name',
  awaySeed: 'away.seed',
  homeSeed: 'home.seed',
  locationName: 'location.name',
  ls: 'ls',
  matchNumber: 'matchNumber',
  pendingAwayOriginalSeed: 'pending.away.originalSeed',
  pendingAwayMatchNumber: 'pending.away.matchNumber',
  pendingHomeOriginalSeed: 'pending.home.originalSeed',
  pendingHomeMatchNumber: 'pending.home.matchNumber',
  pendingAwaySeed: 'pending.away.seed',
  pendingAwayName: 'pending.away.name',
  pendingHomeSeed: 'pending.home.seed',
  pendingHomeName: 'pending.home.name',
  _resultsScoreAway: '_results.score.away',
  _resultsScoreHome: '_results.score.home',
  videoUrl: 'videoUrl'
});

const _mdp_dates = [matchPropTypes.startDate, matchPropTypes.startTime];
const _mdp_homeAways = [matchPropTypes.awayName, matchPropTypes.homeName];
const _mdp_opp = [matchPropTypes.opponentName];
const _mdp_homeAways_seeds = [matchPropTypes.awaySeed, matchPropTypes.awayName, matchPropTypes.homeName, matchPropTypes.homeSeed];
const _mdp_loc_ls_mn = [matchPropTypes.locationName, matchPropTypes.ls, matchPropTypes.matchNumber];
const _mdp_loc_mn = [matchPropTypes.locationName, matchPropTypes.matchNumber];
const _mdp_ply_pendings = [
  matchPropTypes.pendingAwayOriginalSeed,
  matchPropTypes.pendingAwayMatchNumber,
  matchPropTypes.pendingHomeOriginalSeed,
  matchPropTypes.pendingHomeMatchNumber
];
const _mdp_ply_pendingsA = [
  matchPropTypes.pendingAwayOriginalSeed,
  matchPropTypes.pendingAwayMatchNumber,
  matchPropTypes.homeName,
  matchPropTypes.homeSeed
];
const _mdp_pendings = [
  matchPropTypes.pendingAwaySeed,
  matchPropTypes.pendingAwayName,
  matchPropTypes.pendingHomeSeed,
  matchPropTypes.pendingHomeName
];
const _mdp_pendingsA = [
  matchPropTypes.pendingAwaySeed,
  matchPropTypes.pendingAwayName,
  matchPropTypes.homeName,
  matchPropTypes.homeSeed
];
const _mdp_rez = [[matchPropTypes._resultsScoreAway, matchPropTypes._resultsScoreHome]];
const _mdp_startDate_ls = [matchPropTypes.startDate, matchPropTypes.ls];
const _mdp_startDate = [matchPropTypes.startDate];
const _mdp_startTime = [matchPropTypes.startTime];
const _mdp_locationName = [matchPropTypes.locationName];
const _mdp_vid = [matchPropTypes.videoUrl];


const _cnOpts = {
  amed: 'amed',
  amrz: 'amrz',
  bye: 'bye',
  cmt: 'cmt',
  cld: 'cld',
  mdc: 'mdc',
  min: 'min',
  miz: 'miz',
  mrt: 'mrt',
  mmd: 'mmd',
  msc: 'msc',
  msn: 'msn',
  ply: 'ply',
  pnd: 'pnd',
  pnda: 'pnda',
  pndi: 'pndi',
  rez: 'rez',
  rmd: 'rmd',
  rmdish: 'rmdish',
  rrt: 'rrt',
  smo: 'smo',
  fav: 'fav',
}

export const _matchDisplayProps = {
  levelMatches_playoffs: [..._mdp_homeAways_seeds, ..._mdp_rez, ..._mdp_loc_ls_mn],
  levelMatches: [..._mdp_homeAways, ..._mdp_rez],
  levelMatches_vid: [..._mdp_homeAways, ..._mdp_rez, ..._mdp_vid],
  levelMatchNoScore_playoffs: [..._mdp_homeAways_seeds, ..._mdp_startTime, ..._mdp_loc_ls_mn],
  levelMatchNoScore: [..._mdp_homeAways, ..._mdp_startTime],

  levelOverviewMatches: [..._mdp_opp, ..._mdp_rez, ..._mdp_startDate],

  playoff_grid_pending: ['ls', ..._mdp_ply_pendings, ..._mdp_dates, ..._mdp_loc_mn],
  playoff_grid_pendingA: ['ls', ..._mdp_ply_pendingsA, ..._mdp_dates, ..._mdp_loc_mn],
  playoff_grid_noScore_pending: ['ls', ..._mdp_pendings, ..._mdp_dates, ..._mdp_loc_mn],
  playoff_grid_noScore_pendingA: ['ls', ..._mdp_pendingsA, ..._mdp_dates, ..._mdp_loc_mn],
  playoff_grid_noScore: ['ls', ..._mdp_homeAways_seeds, ..._mdp_dates, ..._mdp_loc_mn],
  playoff_grid: ['ls', ..._mdp_homeAways_seeds, '_results.score.away', '_results.score.home', ..._mdp_dates, ..._mdp_loc_mn],
  playoff_noScore: [..._mdp_homeAways_seeds, ..._mdp_loc_ls_mn, ..._mdp_startTime],

  playoff: [..._mdp_homeAways_seeds, ..._mdp_rez, ..._mdp_loc_ls_mn],
  playoffMatchAwayPending: [..._mdp_pendingsA, ..._mdp_loc_ls_mn, ..._mdp_startTime],
  playoffMatchPending: [..._mdp_pendings, ..._mdp_loc_ls_mn, ..._mdp_startTime],
  playoffPending: [..._mdp_homeAways_seeds, ..._mdp_rez, ..._mdp_loc_ls_mn],
  playoffPendingAway: [..._mdp_homeAways, 'home.seed', ..._mdp_rez, ..._mdp_loc_ls_mn],

  scoreboard_playoffs: [..._mdp_loc_ls_mn, ..._mdp_homeAways_seeds, ..._mdp_rez],
  scoreboard: [..._mdp_homeAways, ..._mdp_rez],

  teamMatches_playoffs: [..._mdp_homeAways_seeds, ..._mdp_rez, ..._mdp_startDate_ls, ..._mdp_locationName],
  teamMatches: [..._mdp_homeAways, ..._mdp_rez, ..._mdp_startDate_ls],
  teamMatchesOpp: [..._mdp_startDate_ls, ..._mdp_opp, ..._mdp_startTime],
  teamMatchesOpp_rez: [..._mdp_startDate_ls, ..._mdp_opp, ..._mdp_rez],
  teamMatchesNoScore_playoffs: [..._mdp_homeAways_seeds, ..._mdp_startDate_ls, ..._mdp_startTime, ..._mdp_locationName],
  teamMatchesNoScore: [..._mdp_homeAways, ..._mdp_startDate_ls, ..._mdp_startTime],
  teamMatchNoScore: [..._mdp_homeAways, ..._mdp_startDate_ls, ..._mdp_startTime],
};

const MatchContent = (props) => {

  const {
    matchDisplayType = _matchDisplayTypes.levelMatches,
    matchKey,
    parentKey,
    teamKey,
    matchScheduleDisplayType,
    oppsOnly,
  } = props

  const [isVisible, setIsVisible] = useState(true);

  const setRef = useCallback(node => {
    if (node) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { rootMargin: '300px 0px' }
      );

      observer.observe(node);
    }
  }, []);

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {};
  const { paps_state } = states ?? {};
  const { pathViews } = paps_state ?? {};
  const { navigation_fns } = fns ?? {};

  const _teamKey = teamKey ? teamKey : pathViews?.teams

  // sportsSeasonContext
  const sportsSeasonContext = useContext(SportsSeasonContext);
  const { sportsSeason_state } = sportsSeasonContext ?? {};
  const { latestSeason, matches_info } = sportsSeason_state ?? {};

  // createPlayoffContext
  const createPlayoffContext = useContext(CreatePlayoffContext);
  const { createPlayoff_state } = createPlayoffContext ?? {};

  // matchDataUpdateContext
  const sportsDataContext = useContext(SportsDataContext);
  const { sportsData_handlers } = sportsDataContext ?? {};

  // sportsMatchContext
  const sportsMatchContext = useContext(SportsMatchContext)
  const { sportsMatch_state } = sportsMatchContext ?? {}
  const { match_true, matchAllows } = sportsMatch_state ?? {}
  const { edit: _allowEdit_match, scores: _allowEdit_scores } = matchAllows ?? {}

  const { matches_realtime, matches_matchDay, matchPlayoffPendings } = matches_info ?? {}
  const { _favTeamKey, _results, _status, _matchChanges, _results_matchDay, _results_realtime, matchStatus, isPlayoff, _scorer, _isCancelled } = match_true ?? {}

  const { matchUrl } = useMatchVideos(match_true)

  let _results_combined = _results ? _.cloneDeep(_results) : {}

  function combineResults(r, rmd, rrt) {

    const hasScore = !_.isEmpty(r?.score);
    const hasScores = !_.isEmpty(r?.scores);

    let result;

    if (!hasScore && !hasScores) {
      if (rrt) {
        result = _.merge({}, r, rrt);
        result._resultsFrom = "realtime";
      }
      if (rmd) {
        result = _.merge({}, r, rmd);
        result._resultsFrom = "matchDay";
      }
    } else {
      result = _.cloneDeep(r);
    }

    return result;
  }

  if (_results_matchDay || _results_realtime) {
    _results_combined = combineResults(_results, _results_matchDay, _results_realtime)
  }

  const match_pending = matchPlayoffPendings?.[matchKey]

  if (match_true && match_pending && match_pending.home && match_pending.home && _status) {
    match_true.home = match_pending.home
    match_true.away = match_pending.away
    _status.isPending = false
    _status.isPendingish = true
  }

  if (match_true && !match_true.home && !match_true.away && match_true.pending) {
    match_true.home = match_true.pending.home
    match_true.away = match_true.pending.away
    if (!_status) {
      // _status.isPending = false
      // _status.isPendingish = true
    }

  }

  const { isMissing, isBye, isPending, isPendingish, isPendingAway } = _status ?? {}
  const { matchCn, complete, winner } = _results_combined ?? {}

  // if (_results_matchDay) {
  //   if (!_results_combined?.score && !_results?.scores) {
  //     match_true._results = _results_matchDay
  //     match_true._resultsFrom = 'matchDay'
  //   } else {
  //     match_true._resultsFrom = 'matchDayish'
  //   }
  // }

  // if (_results_realtime && !_results?.score && !_results?.scores) {
  //   match_true._results = _results_realtime
  //   match_true._resultsFrom = 'realtime'
  // }

  const hasScore = !_.isEmpty(_results_combined?.score)
  const hasScores = !_.isEmpty(_results_combined?.scores)
  const hasTrueScore = hasScores || hasScore

  let showScores;

  const [results_realtime, set_results_realtime] = useState()
  const [results_matchDay, set_results_matchDay] = useState()
  const [showMatchOptions, setShowMatchOptions] = useState()
  const [showResultOptions, setShowResultOptions] = useState()

  let _matchPropType = matchDisplayType

  const { mpt } = useMatchDisplayType({ match_true, matchUrl, results_matchDay, matchDisplayType: props.matchDisplayType })

  let _forGrid = _matchPropType?.includes('grid');

  const { _isPending, _isPendingA } = useMemo(() => {
    const _matchPlayoffPendings = matchFilterHelpers.matchesWithPendings(createPlayoff_state?.playoff_matchInfo?.matches)
    const pendingMatch = _matchPlayoffPendings?.[match_true?._itemKey]
    const _isPending = pendingMatch && !pendingMatch.home ? true : false
    const _isPendingA = pendingMatch && pendingMatch.home ? true : false
    return { _isPending, _isPendingA }
  }, [match_true])

  // if (!complete && !_matchPropType?.includes('grid')) {
  //   switch (matchDisplayType) {
  //     case _matchDisplayTypes.teamMatches:
  //       _matchPropType = _matchDisplayTypes.teamMatchesNoScore
  //       break;
  //     default:
  //       if (isPending) {
  //         _matchPropType = _matchDisplayTypes.playoffMatchPending
  //       } else if (isPendingAway) {
  //         _matchPropType = _matchDisplayTypes.playoffMatchAwayPending
  //       } else {
  //         if (!results_matchDay) {
  //           _matchPropType = _matchDisplayTypes.levelMatchNoScore
  //         }
  //       }
  //   }
  // }

  // if (isPlayoff) {
  //   switch (_matchPropType) {
  //     case _matchDisplayTypes.levelMatchNoScore:
  //     case _matchDisplayTypes.levelMatches:
  //     case _matchDisplayTypes.teamMatches:
  //     case _matchDisplayTypes.teamMatchesNoScore:
  //     case _matchDisplayTypes.scoreboard:
  //       _matchPropType = _matchPropType + '_playoffs';
  //       break;
  //   }
  // }

  // if (_matchPropType === 'levelMatches' && matchUrl) {
  //   _matchPropType = _matchPropType + '_levelMatches_vid'
  // }

  switch (matchDisplayType) {
    case _matchDisplayTypes.playoff_grid:
      showScores = false
    // if (!hasScores && !hasScore) {
    //   if (_isPending) {
    //     _matchPropType = _matchDisplayTypes.playoff_grid_pending
    //   } else if (_isPendingA) {
    //     _matchPropType = _matchDisplayTypes.playoff_grid_pendingA
    //   } else if (isPending) {
    //     _matchPropType = _matchDisplayTypes.playoff_grid_noScore_pending
    //   } else if (isPendingAway) {
    //     _matchPropType = _matchDisplayTypes.playoff_grid_noScore_pendingA
    //   } else {
    //     _matchPropType = _matchDisplayTypes.playoff_grid_noScore
    //   }
    // }
    // break;
    case _matchDisplayTypes.levelOverviewMatches:
      showScores = false
      break;
    default:
      showScores = hasScores || results_realtime || results_matchDay
  }

  switch (matchScheduleDisplayType) {
    case gEnums.matchScheduleDisplayTypes.currentMatches:
    case gEnums.matchScheduleDisplayTypes.matchDay:
    case gEnums.matchScheduleDisplayTypes.missing:
    case gEnums.matchScheduleDisplayTypes.pendingMatches:
      showScores = false
      break;
    default:
  }

  const matchRealTime = useMemo(() => {
    return matches_realtime?.[match_true?._itemKey] || null;
  }, [matches_realtime, match_true?._itemKey]);

  const matchMatchDay = useMemo(() => {
    return matches_matchDay?.[match_true?._itemKey] || null;
  }, [matches_matchDay, match_true?._itemKey]);

  useEffect(() => {
    switch (_matchPropType) {
      case _matchDisplayTypes.levelMatchNoScore_playoffs:
      case _matchDisplayTypes.levelMatchNoScore:
        if (matchRealTime) {
          set_results_realtime(match_true?._results_realtime);
        } else {
          set_results_realtime()
        }
    }
  }, [matchRealTime]);

  useEffect(() => {
    if (matchMatchDay) {
      set_results_matchDay(match_true?._results_matchDay);
    } else {
      set_results_matchDay()
    }
  }, [matchMatchDay]);

  const allowEdits = useMemo(() => {
    switch (matchDisplayType) {
      case _matchDisplayTypes.scoreboard:
      case _matchDisplayTypes.teamMatches:
      case _matchDisplayTypes.levelMatches:
        if (_allowEdit_match || _allowEdit_scores) {
          return true
        }
    }
  }, [match_true, _allowEdit_match, _allowEdit_scores])

  const handle_updateMatchFromResults = (e) => {
    const cb = (r) => {
      setShowResultOptions()
    }
    e && e.stopPropagation()

    const _match_true = _.cloneDeep(match_true)
    const { _matchCollectionKey, _itemKey, isPlayoff, _results_matchDay } = _match_true
    _match_true.results = {
      score: _results_matchDay.score
    }
    delete _match_true._resultsFrom
    delete _match_true._scorer
    const prefix = isPlayoff ? 'playoffMatch' : 'match'
    const currentLevelMatches = {
      [_matchCollectionKey]: {
        [prefix + '.' + _itemKey]: _match_true
      }
    }
    sportsData_handlers.handleUpdate_matchesByLevel(currentLevelMatches, isPlayoff, cb, latestSeason)
  }

  const handle_cancelMatchFromResults = (e) => {
    e && e.stopPropagation()
    setShowResultOptions()
  }

  const handleShow_resultOptions = (e, mrt) => {
    e && e.stopPropagation()
    if (_allowEdit_scores) {
      setShowResultOptions(mrt)
    }
  }

  /**
   * Only appears if allowEdits is true
   * @param {object} e 
   */
  // const handleShow_matchOptions = (e, m, p) => {
  //   e && e.stopPropagation()
  //   switch (matchScheduleDisplayType) {
  //     case gEnums.matchScheduleDisplayTypes.currentMatches:
  //     case gEnums.matchScheduleDisplayTypes.matchDay:
  //     case gEnums.matchScheduleDisplayTypes.missing:
  //       break;
  //     case gEnums.matchScheduleDisplayTypes.pendingMatches:
  //       switch (p) {
  //         case 'location.name':
  //           sportsData_handlers.handleShow_matchEdit(p, m)
  //           break;
  //         case 'startTime':
  //           sportsData_handlers.handleShow_matchEdit(p, m)
  //           break;
  //         default:
  //         // nothing
  //       }

  //       break;
  //     default:
  //       setShowMatchOptions(!showMatchOptions)
  //   }
  // }

  const handleMatchClick = (e) => {
    e.stopPropagation()
    if (createPlayoff_state) return
    switch (matchScheduleDisplayType) {
      case gEnums.matchScheduleDisplayTypes.currentMatches:
      case gEnums.matchScheduleDisplayTypes.matchDay:
      case gEnums.matchScheduleDisplayTypes.missing:
      case gEnums.matchScheduleDisplayTypes.pendingMatches:
        break;
      default:
        if (match_true) navigation_fns.handleNav_item({ key: 'matches' }, match_true._itemKey)
    }
  }

  const cnn = () => {

    let _cn;

    switch (matchDisplayType) {
      case _matchDisplayTypes.playoff_grid_noScore_pending:
      case _matchDisplayTypes.playoff_grid_noScore_pendingA:
      case _matchDisplayTypes.playoff_grid_noScore:
      case _matchDisplayTypes.playoff_grid:
        _cn = ['match-grid']
        break;
      default:
        _cn = ['match-wrapper']
    }

    if (matchCn) _cn.push(matchCn)

    if (_matchChanges?.dateChanged) _cn.push(_cnOpts.mdc)
    if (_matchChanges?.matchIsNew) _cn.push(_cnOpts.min)
    if (_matchChanges?.matchStatusChanged || matchStatus === 'canceled') _cn.push(_cnOpts.cld)

    if ((winner || results_matchDay) && !isBye) _cn.push(_cnOpts.rez)
    if (isMissing && !isBye && !results_matchDay) _cn.push(_cnOpts.miz)

    if (_results_combined?._resultsFrom === 'matchDay') _cn.push(_cnOpts.rmd)
    if (_results_combined?._resultsFrom === 'matchDayish') _cn.push(_cnOpts.rmdish)
    if (_results_combined?._resultsFrom === 'realtime') _cn.push(_cnOpts.rrt)

    if (winner && [gEnums.matchScheduleDisplayTypes.teamSchedule, gEnums.matchScheduleDisplayTypes.levelOverviewMatches].includes(matchScheduleDisplayType)) {
      _cn.push(winner?.id === _teamKey ? 'win' : 'loss')
    }

    if (allowEdits && _allowEdit_scores) _cn.push(_cnOpts.amrz)
    if (allowEdits && _allowEdit_match) _cn.push(_cnOpts.amed)

    // hfcvb@2025!

    if (showMatchOptions) {
      _cn.push(_cnOpts.smo)
    } else {
      if (!hasTrueScore) {
        if (results_realtime) {
          _cn.push(_cnOpts.mrt)
        }
        // if (results_matchDay) {
        //   _cn.push(_cnOpts.mmd)
        // }
      }
    }

    if (complete) {
      _cn.push(_cnOpts.cmt)
    }

    if (isPlayoff) {
      _cn.push(_cnOpts.ply)
      if (isBye) {
        _cn.push(_cnOpts.bye)
      }
      if (isPending) {
        _cn.push(_cnOpts.pnd)
      } else if (isPendingAway) {
        _cn.push(_cnOpts.pnda)
      } else if (isPendingish) {
        _cn.push(_cnOpts.pndi)
      }
    }

    if (_isCancelled) {
      _cn.push(_cnOpts.cld)
    }

    if (_favTeamKey) {
      _cn.push(_cnOpts.fav)
    }

    return _cn.join(' ')

  }

  // get all the divs for the match based on the matchDisplayType and matchDisplayProps
  const matchDivs = useMemo(() => {

    const mdps = _matchDisplayProps[mpt]

    let _mdps = mdps

    if (oppsOnly &&
      matchDisplayType === _matchDisplayTypes.teamMatches &&
      (matchScheduleDisplayType === gEnums.matchScheduleDisplayTypes.teamSchedule ||
        matchScheduleDisplayType === gEnums.matchScheduleDisplayTypes.nextTeamMatch
      )) {
      const mdpso = _matchDisplayProps[complete ? 'teamMatchesOpp_rez' : 'teamMatchesOpp']
      _mdps = mdpso
    }

    return <MatchDivs
      _teamKey={_teamKey}
      allowEdits={allowEdits}
      match={match_true}
      matchDisplayType={matchDisplayType}
      matchScheduleDisplayType={matchScheduleDisplayType}
      mdps={_mdps}
      results_matchDay={results_matchDay}
      matchUrl={matchUrl}
    />
  }, [match_true])


  const results_true = () => {
    return <div className='match-rez'>
      <div className={'mrl-c'}><Icon name={'check'} color='green' /></div>
      <div className={'mrl'}>
        <MatchScores results={_results_combined} results_matchDay={results_matchDay} />
      </div>
      <div className={'mrl-s'} >{'O'}</div>
    </div>
  }

  const results_md = () => {
    if (showResultOptions) {
      return <div className='match-mdr'>
        {mropts()}
      </div>
    } else {
      return <div className='match-mdr'>
        <div className={'mrl-c'}><Icon name={'calendar outline'} color='blue' /></div>
        <div className={'mrl'}>
          <MatchScores results={results_matchDay} />
        </div>
        <div className={'mrl-s'} onClick={(e) => { handleShow_resultOptions(e, 'md') }}><Label size='mini'>
          <Icon color='blue' name='user' /> MDR
        </Label></div>
      </div>
    }
  }

  const results_rt = () => {
    return <div className='match-rtr'>
      <div className={'mrl-c'}><Icon className={'blink_rez'} name={'clock outline'} color='blue' /></div>
      <div className={'mrl'}>
        <MatchScores results={results_realtime} />
      </div>
      <div className={'mrl-s'} onClick={(e) => { handleShow_resultOptions(e, 'rt') }}><Label size='mini'>
        <Icon color='blue' name='user' /> {_scorer ? _scorer : 'S'}
      </Label></div>
      {/* <div className={'mrl-s'} onClick={(e) => { handleShow_resultOptions(e, 'rt') }}>{_scorer ? _scorer : 'S'}</div> */}
    </div>
  }

  const mropts = () => <div className='match-score-btns opts'>
    <div>
      <Button size='mini' icon='delete' color={'black'} onClick={(e) => { handle_cancelMatchFromResults(e) }} >{'Cancel'}</Button>
      <Button size='mini' icon='save' color={_allowUpdate_local ? 'green' : 'teal'} onClick={(e) => { handle_updateMatchFromResults(e) }} >{'Update Results'}</Button>
    </div>
  </div>

  const matchContent = useMemo(() => {
    if (!match_true) return <div></div>;
    return <LongPressWrapper
      itemData={match_true}
      longPressType={_longPressTypes.match}
      longPressModal={{ content: <MatchResultSquares match_true={match_true} /> }}
      onClick={handleMatchClick}
    >
      <div
        ref={setRef}
        key={parentKey ? uniqueKey(parentKey, matchKey) : uniqueKey(matchKey)}
        className={cnn()}
      >
        {isVisible && matchDivs}
        {showScores && hasScores && !_forGrid && results_true()}
      </div>
    </LongPressWrapper>
  }, [match_true])

  return matchContent
}

export default MatchContent

/**
 * 
 * @param {*} props 
 * @returns a single match 
 */
export const MatchContentWithProvider = (props) => {
  return <SportsMatchProvider {...props}>
    <SportsRtMatchProvider {...props}>
      <MatchContent {...props} />
    </SportsRtMatchProvider>
  </SportsMatchProvider>
}